// Define the game rules for this specific game

import { GameAssets } from "./shared/util/GameAssets";
import { InfoPopup } from "./shared/UI/InfoPopup";
import gsap from "gsap";

// Can be re-styled and repurposed for other games
export const getGameRuleHtmlElement: () => HTMLElement = () => {
  const root: HTMLElement = document.createElement("div");

  Object.assign(root.style, {
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    alignItems: "center",
    padding: "20px",
    position: "relative",
    overflowY: "scroll",
    overflowX: "hidden",
    width: "100%",
    height: "100%",
    zIndex: "9998",
  });

  const createHorizontalRuler: () => HTMLElement = () => {
    let horizontalRuler: HTMLElement = document.createElement("hr");

    Object.assign(horizontalRuler.style, {
      // Ensure flex layout won't shrink our hr-s
      flexShrink: "0",
      width: "100%",
      height: "2px",
      background:
        "linear-gradient(to right, #00000000 0%, #555 33%, #555 66%, #00000000 100%)",
      border: "0px",
      zIndex: "9998",
    });

    return horizontalRuler;
  };

  const createHeader: (text: string) => HTMLElement = (text: string) => {
    const header: HTMLElement = document.createElement("h4");

    Object.assign(header.style, {
      display: "block",
      position: "relative",
      margin: "0.25em 0em 0.25em 0em",
      color: "rgb(255, 133, 49)",
      zIndex: "9998",
    });

    header.textContent = text.replace(/\s+/g, " ");

    return header;
  };

  const createParagraph: (text: string) => HTMLElement = (text: string) => {
    const paragraph: HTMLElement = document.createElement("p");

    Object.assign(paragraph.style, {
      display: "block",
      position: "relative",
      fontSize: "0.7em",
      textAlign: "center",
      color: "white",
      zIndex: "9998",
    });

    text = text.trim();
    paragraph.innerHTML = text;

    return paragraph;
  };

  const createBulletList: (
    points: string[],
    showBullets?: boolean,
    padding?: string,
    textAlign?: "left" | "center",
  ) => HTMLElement = (
    points: string[],
    showBullets: boolean = true,
    padding: string = "0",
    textAlign: "left" | "center" = "left",
  ) => {
    const bulletList: HTMLElement = document.createElement("ul");

    Object.assign(bulletList.style, {
      display: "block",
      padding: padding,
      margin: "0",
      position: "relative",
      fontSize: "0.7em",
      listStyleType: showBullets ? "disc" : "none",
      color: "white",
      textAlign: textAlign,
      zIndex: "9998",
    });

    points.forEach((point: string) => {
      const bulletPoint: HTMLElement = document.createElement("li");
      bulletPoint.innerHTML = point;
      bulletList.appendChild(bulletPoint);
    });

    return bulletList;
  };

  const createImage: (
    path: string,
    width?: string,
    height?: string,
    margin?: string,
  ) => HTMLImageElement = (
    path: string,
    width: string = "auto",
    height: string = "6em",
    margin: string = "0",
  ) => {
    const image: HTMLImageElement = document.createElement("img");

    Object.assign(image.style, {
      display: "block",
      position: "relative",
      margin: margin,
      width: width,
      height: height,
      aspectRatio: "auto",
      objectFit: "cover",
      zIndex: "9998",
    });

    image.src = path;

    return image;
  };

  const createAtlasImage = (
    spriteName: string,
    atlas: any,
    width: string = "auto",
    height: string = "6em",
    margin: string = "0",
  ) => {
    const div = document.createElement("div");
    const atlasData: any = atlas;
    const spriteData: any = atlasData.textures[spriteName];

    if (!spriteData) {
      console.error(`Sprite ${spriteName} not found in atlas`);
      return div;
    }

    const updateStyle = () => {
      const fontSize: number = parseFloat(
        InfoPopup.getComputedStyle().fontSize,
      );
      // console.log(fontSize);
      const frame = spriteData.frame;

      const atlasWidth = atlasData.textureSource.pixelWidth;
      const atlasHeight = atlasData.textureSource.pixelHeight;

      // Calculate sprite aspect ratio
      const spriteAspectRatio = frame.width / frame.height;

      // Convert em to pixels
      let containerHeight = parseFloat(height) * fontSize;

      // Calculate width based on height and sprite aspect ratio
      let containerWidth: number;
      let actualWidth: string;

      if (width === "auto") {
        containerWidth = containerHeight * spriteAspectRatio;
        actualWidth = `${containerWidth / fontSize}em`; // Convert back to em
      } else if (width.endsWith("%")) {
        const parentWidth =
          div.parentElement?.getBoundingClientRect().width || 400;
        containerWidth = (parseFloat(width) / 100) * parentWidth;
        actualWidth = width;
      } else {
        containerWidth = parseFloat(width) * fontSize;
        actualWidth = width;
      }

      if (height === "auto") {
        containerHeight = containerWidth / spriteAspectRatio;
      } else {
        containerHeight = parseFloat(height) * fontSize;
      }

      // Calculate scale to fit sprite in container
      const scaleX = containerWidth / frame.width;
      const scaleY = containerHeight / frame.height;
      const scale = Math.min(scaleX, scaleY); // Maintain aspect ratio

      // Calculate background position (scaled)
      const bgX = -frame.x * scale;
      const bgY = -frame.y * scale;

      Object.assign(div.style, {
        display: "block",
        flexShrink: "0",
        position: "relative",
        margin: margin,
        width: actualWidth,
        height: height === "auto" ? `${containerHeight / fontSize}em` : height,
        backgroundImage: `url(${spriteData._source._sourceOrigin})`,
        backgroundPosition: `${bgX}px ${bgY}px`,
        backgroundSize: `${atlasWidth * scale}px ${atlasHeight * scale}px`,
        backgroundRepeat: "no-repeat",
        zIndex: "9998",
      });
    };

    // Update the style upon creation, and upon changing window size
    updateStyle();
    gsap.delayedCall(0.8, () => {
      updateStyle();
    });
    window.addEventListener("resize", () => {
      updateStyle();
    });

    return div;
  };

  // const createGrid: (
  //   elements: HTMLElement[],
  //   rows: number,
  //   columns: number,
  // ) => HTMLElement = (
  //   elements: HTMLElement[],
  //   rows: number,
  //   columns: number,
  // ) => {
  //   const grid: HTMLElement = document.createElement("div");

  //   Object.assign(grid.style, {
  //     display: "grid",
  //     justifyContent: "center",
  //     gap: "0px",
  //     gridTemplateRows: `repeat(${rows}, 1fr)`,
  //     gridTemplateColumns: `repeat(${columns}, 1fr)`,
  //     position: "relative",
  //     zIndex: "9998",
  //   });

  //   elements.forEach((element: HTMLElement) => {
  //     grid.appendChild(element);
  //   });

  //   return grid;
  // };

  const createHorizontalElementSequence: (
    elements: HTMLElement[],
    width: string,
    elementWidth: string,
  ) => HTMLElement = (
    elements: HTMLElement[],
    width: string,
    elementWidth: string,
  ) => {
    const container: HTMLElement = document.createElement("div");

    Object.assign(container.style, {
      display: "flex",
      width: width,
      flexWrap: "wrap",
      justifyContent: "center",
      alignItems: "baseline",
      position: "relative",
      zIndex: "9998",
    });

    elements.forEach((element: HTMLElement) => {
      Object.assign(element.style, {
        width: elementWidth,
      });
      container.appendChild(element);
    });

    return container;
  };

  const createCard: (elements: HTMLElement[]) => HTMLElement = (
    elements: HTMLElement[],
  ) => {
    const card: HTMLElement = document.createElement("div");

    Object.assign(card.style, {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      position: "relative",
      boxSizing: "border-box",
      zIndex: "9998",
    });

    elements.forEach((element: HTMLElement) => {
      card.appendChild(element);
    });

    return card;
  };

  root.appendChild(createHorizontalRuler());
  root.appendChild(createHeader("Paytable"));
  root.appendChild(
    createParagraph(
      `Gannicus is a 5x3 video slot with 20 Bet lines.<br>
        The game features Random Multiplier Wilds. Free Spins Bonus and Super Free Spins Bonus.<br> 
        The maximum win is 6000x bet!`,
    ),
  );

  root.appendChild(createHorizontalRuler());
  root.appendChild(createHeader("Wild Symbol"));
  root.appendChild(
    createAtlasImage("Wild.webp", GameAssets.textures.symbolAtlas),
  );
  root.appendChild(
    createParagraph(
      `Two symbols can appear on any spin and there is a chance they will have a multiplier on them.<br> 
        The values of the multiplier are: 2, 3, 5, and 20.<br> 
        The Wild symbols will appear and then move to a random reel position.<br> 
        If the Wild symbol has a multiplier and it substitues another symbol, then it will multiply the win by the associated multiplier value.<br> 
        If two or more Wild symbols with multipliers are on a winning bet line, the multipliers will add together.`,
    ),
  );

  root.appendChild(createHorizontalRuler());
  root.appendChild(createHeader("Free Spins Bonus"));
  root.appendChild(
    createHorizontalElementSequence(
      [
        createAtlasImage(
          "Bonus.webp",
          GameAssets.textures.symbolAtlas,
          "auto",
          "5em",
          "0.5em",
        ),
        createAtlasImage(
          "Bonus.webp",
          GameAssets.textures.symbolAtlas,
          "auto",
          "5em",
          "0.5em",
        ),
        createAtlasImage(
          "Bonus.webp",
          GameAssets.textures.symbolAtlas,
          "auto",
          "5em",
          "0.5em",
        ),
      ],
      "100%",
      "25%",
    ),
  );
  root.appendChild(
    createParagraph(
      `The Free Spins Bonus is triggered by landing 3 or more Bonus symbols on a single spin.<br>
        Any additional Bonus symbols landing during the Free Spins Bonus are collected to help advance through the five stages.<br>
        Triggering the Free Spins Bonus feature with 3, 4, or 5 Bonus symbols will start the Bonus collection at 3, 4, or 5 respectively.<br>
        <br>
        Starting with 5 Free Spins and two Roaming Wild symbols, the Roaming Wild symbols will remain on the reels for the remainder of the Free Spins Bonus, moving to new positions before each new Free Spin.<br>
        Landing 3 or more Bonus symbols on a single spin in the Free Spins Bonus will award 5 extra Free Spins on the current stage.`,
    ),
  );
  root.appendChild(
    createImage(GameAssets.textures.wildsDemo.label, "100%", "3em"),
  );
  root.appendChild(
    createParagraph(
      `To unlock stages:<br>
        Stage 2: Collect 6 Bonus symbols in total to gain 5 additional Free Spins with X2 Roaming Multiplier Wilds.<br>
        Stage 3: Collect 9 Bonus symbols in total to gain 5 additional Free Spins with X3 Roaming Multiplier Wilds.<br>,
        Stage 4: Collect 13 Bonus symbols in total to gain 5 additional Free Spins with X5 Roaming Multiplier Wilds.<br>,
        Stage 5: Collect 17 Bonus symbols in total to gain 5 additional Free Spins with X20 Roaming Multiplier Wilds.<br>
        <br>
        Each unlocked stage begins after the previous one ends.<br>
        Bonus symbols collected for each stage include those from previous stages and those that triggered the Free Spins Bonus.`,
    ),
  );

  // rgb(255, 133, 49) orange
  root.appendChild(createHorizontalRuler());
  root.appendChild(
    createCard([
      createAtlasImage(
        "spinButton.webp",
        GameAssets.textures.uiAtlas,
        "auto",
        "3em",
        "0.5em",
      ),
      createBulletList(["Spin button."], false, "0", "center"),
    ]),
  );
  root.appendChild(createHorizontalRuler());
  root.appendChild(
    createCard([
      createAtlasImage(
        "buyButton.webp",
        GameAssets.textures.uiAtlas,
        "auto",
        "3em",
        "0.5em",
      ),
      createBulletList(["Cheat button."], false, "0", "center"),
    ]),
  );
  root.appendChild(createHorizontalRuler());
  root.appendChild(
    createCard([
      createAtlasImage(
        "soundOn.webp",
        GameAssets.textures.uiAtlas,
        "auto",
        "3em",
        "0.5em",
      ),
      createBulletList(["Sound toggle on/off."], false, "0", "center"),
    ]),
  );
  root.appendChild(createHorizontalRuler());
  root.appendChild(
    createCard([
      createAtlasImage(
        "autoplayButton.webp",
        GameAssets.textures.uiAtlas,
        "auto",
        "3em",
        "0.5em",
      ),
      createBulletList(
        ["Allows specifying the amount of times autoplay will be executed."],
        false,
        "0",
        "center",
      ),
    ]),
  );
  root.appendChild(createHorizontalRuler());
  root.appendChild(
    createCard([
      createAtlasImage(
        "fastOn.webp",
        GameAssets.textures.uiAtlas,
        "auto",
        "3em",
        "0.5em",
      ),
      createBulletList(["Makes the spins faster."], false, "0", "center"),
    ]),
  );
  root.appendChild(createHorizontalRuler());
  root.appendChild(
    createCard([
      createImage(GameAssets.textures.betDemo.label, "auto", "2em", "0.5em"),
      createBulletList(
        [
          "Allows setting a custom bet amount. Cannot be changed during a spin. Max bet allowed is 100 EUR.",
        ],
        false,
        "0",
        "center",
      ),
    ]),
  );

  root.appendChild(createHorizontalRuler());
  root.appendChild(createHeader("Symbols Pay (EUR)"));
  root.appendChild(
    createHorizontalElementSequence(
      [
        createCard([
          createAtlasImage(
            "Bonus.webp",
            GameAssets.textures.symbolAtlas,
            "auto",
            "5em",
            "0.2em",
          ),
          createBulletList(["Bonus"], false),
        ]),
        createCard([
          createAtlasImage(
            "Wild.webp",
            GameAssets.textures.symbolAtlas,
            "auto",
            "5em",
            "0.2em",
          ),
          createBulletList(
            [
              "Wild",
              "<br>",
              "5: <span style='color: orange'>40</span>",
              "4: <span style='color: orange'>10</span>",
              "3: <span style='color: orange'>2.5</span>",
            ],
            false,
          ),
        ]),
        createCard([
          createAtlasImage(
            "Gannicus.webp",
            GameAssets.textures.symbolAtlas,
            "auto",
            "5em",
            "0.2em",
          ),
          createBulletList(
            [
              "5: <span style='color: orange'>40</span>",
              "4: <span style='color: orange'>10</span>",
              "3: <span style='color: orange'>2.5</span>",
            ],
            false,
          ),
        ]),
      ],
      "100%",
      "25%",
    ),
  );
  root.appendChild(
    createHorizontalElementSequence(
      [
        createCard([
          createAtlasImage(
            "Gladiator.webp",
            GameAssets.textures.symbolAtlas,
            "auto",
            "5em",
            "0.2em",
          ),
          createBulletList(
            [
              "5: <span style='color: orange'>40</span>",
              "4: <span style='color: orange'>10</span>",
              "3: <span style='color: orange'>2.5</span>",
            ],
            false,
          ),
        ]),
        createCard([
          createAtlasImage(
            "Lion.webp",
            GameAssets.textures.symbolAtlas,
            "auto",
            "5em",
            "0.2em",
          ),
          createBulletList(
            [
              "5: <span style='color: orange'>40</span>",
              "4: <span style='color: orange'>10</span>",
              "3: <span style='color: orange'>2.5</span>",
            ],
            false,
          ),
        ]),
        createCard([
          createAtlasImage(
            "Weapon1.webp",
            GameAssets.textures.symbolAtlas,
            "auto",
            "5em",
            "0.2em",
          ),
          createBulletList(
            [
              "5: <span style='color: orange'>40</span>",
              "4: <span style='color: orange'>10</span>",
              "3: <span style='color: orange'>2.5</span>",
            ],
            false,
          ),
        ]),
        createCard([
          createAtlasImage(
            "Weapon2.webp",
            GameAssets.textures.symbolAtlas,
            "auto",
            "5em",
            "0.2em",
          ),
          createBulletList(
            [
              "5: <span style='color: orange'>40</span>",
              "4: <span style='color: orange'>10</span>",
              "3: <span style='color: orange'>2.5</span>",
            ],
            false,
          ),
        ]),
      ],
      "100%",
      "25%",
    ),
  );
  root.appendChild(
    createHorizontalElementSequence(
      [
        createCard([
          createAtlasImage(
            "A.webp",
            GameAssets.textures.symbolAtlas,
            "auto",
            "5em",
            "0.2em",
          ),
          createBulletList(
            [
              "5: <span style='color: orange'>40</span>",
              "4: <span style='color: orange'>10</span>",
              "3: <span style='color: orange'>2.5</span>",
            ],
            false,
          ),
        ]),
        createCard([
          createAtlasImage(
            "K.webp",
            GameAssets.textures.symbolAtlas,
            "auto",
            "5em",
            "0.2em",
          ),
          createBulletList(
            [
              "5: <span style='color: orange'>40</span>",
              "4: <span style='color: orange'>10</span>",
              "3: <span style='color: orange'>2.5</span>",
            ],
            false,
          ),
        ]),
        createCard([
          createAtlasImage(
            "J.webp",
            GameAssets.textures.symbolAtlas,
            "auto",
            "5em",
            "0.2em",
          ),
          createBulletList(
            [
              "5: <span style='color: orange'>40</span>",
              "4: <span style='color: orange'>10</span>",
              "3: <span style='color: orange'>2.5</span>",
            ],
            false,
          ),
        ]),
        createCard([
          createAtlasImage(
            "Q.webp",
            GameAssets.textures.symbolAtlas,
            "auto",
            "5em",
            "0.2em",
          ),
          createBulletList(
            [
              "5: <span style='color: orange'>40</span>",
              "4: <span style='color: orange'>10</span>",
              "3: <span style='color: orange'>2.5</span>",
            ],
            false,
          ),
        ]),
      ],
      "100%",
      "25%",
    ),
  );
  root.appendChild(
    createParagraph("A symbol's pay reflects current bet configuration."),
  );

  root.appendChild(createHorizontalRuler());
  root.appendChild(createHeader("Bet Lines"));
  root.appendChild(
    createImage(GameAssets.textures.betwaysDemo.label, "100%", "8em"),
  );
  root.appendChild(
    createParagraph(
      `Winning combinations are formed from left to right on adjacent reels, beginning from the leftmost reel.\n<br>
        Only the highest winning combination is paid per line.`,
    ),
  );

  root.appendChild(createHorizontalRuler());
  root.appendChild(createHeader("Rules & Information"));
  root.appendChild(
    createBulletList(
      [
        "Gannicus is a 5x3 video slot with 20 Bet lines.",
        "Wild symbols substitute for all symbols except Bonus, and Multiplier Wild symbols.",
        "The theoretical pay-out (RTP) is 96.08% and maximum win is 6000x bet.",
        "If the total win during a game round reaches 6000x, the game round will be ended and the won amount will be paid out.",
        "Bets are made in the casino currency, and there are 26 bet levels.",
        "The bet level cannot be changed during a game round.",
        "All wins are displayed and paid in the casino currency in accordance with the paytable.",
        "If a game round is interrupted, all information and bets will be displayed when the game is relaunched",
        "Wins from uninterrupted game rounds may be forfeited if they are not claimed within a certain period. Please see operator specific terms and conditions for details",
        "Malfunction voids all plays and pays",
      ],
      true,
      "1em",
    ),
  );

  return root;
};
